import { Reveal } from "./Reveal";

const FOUNDER = "/founder.jpg";
const FOUNDER_NAME = "Xena Redd";
const FOUNDER_TITLE = "Founder & CEO";

const VALUES = [
  {
    n: "01",
    title: "Integrity",
    body: "Total accountability. We handle every review — glowing or difficult — with honesty and care.",
  },
  {
    n: "02",
    title: "Hospitality",
    body: "The warmth of a five-star check-in, applied to every customer interaction on your behalf.",
  },
  {
    n: "03",
    title: "Respect",
    body: "Your reputation, your voice. We protect the brand you've spent years building.",
  },
];

export default function Mission() {
  return (
    <section id="mission" data-testid="mission-founder-section" className="relative bg-background py-24 lg:py-36">
      <div className="container-luxe">
        {/* Mission statement */}
        <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-10 lg:gap-20">
          <Reveal>
            <p className="overline flex items-center gap-3">
              <span className="h-px w-8 bg-[var(--gold)]" />
              Our Mission
            </p>
          </Reveal>
          <div>
            <Reveal>
              <h2 className="serif text-[clamp(1.9rem,3.6vw,3.2rem)] leading-[1.08] tracking-tight text-foreground">
                Rooted in integrity, hospitality,{" "}
                <span className="italic text-gold">and respect.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 max-w-2xl text-lg leading-relaxed text-muted-foreground">
                At Beam Concierge, our mission is to illuminate the hard work of
                small businesses — bridging the gap between exceptional service and
                a flawless digital reputation. We provide the infrastructure and
                trust required to turn everyday interactions into five-star
                recognition, higher search rankings, and sustainable revenue growth.
              </p>
            </Reveal>

            {/* Numbered manifesto */}
            <div className="mt-14 grid sm:grid-cols-3 gap-8 border-t border-border pt-10">
              {VALUES.map((v, i) => (
                <Reveal key={v.n} delay={i * 0.1}>
                  <div data-testid={`value-${v.title.toLowerCase()}`}>
                    <span className="serif text-2xl text-[var(--gold)]">{v.n}</span>
                    <h3 className="serif text-xl mt-2 text-foreground">{v.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                      {v.body}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>

        {/* Founder story */}
        <div className="mt-28 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <div className="group relative overflow-hidden">
              <img
                src={FOUNDER}
                alt="Founder of Beam Concierge"
                data-testid="founder-portrait"
                className="h-[460px] sm:h-[560px] w-full object-cover object-top transition-transform duration-[1200ms] ease-out group-hover:scale-105"
              />
              <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black/50 to-transparent" />
              <div className="absolute bottom-5 left-5">
                <p className="serif text-2xl text-white">{FOUNDER_NAME}</p>
                <p className="text-xs uppercase tracking-[0.24em] text-[var(--gold-light)] mt-1">
                  {FOUNDER_TITLE}
                </p>
                <p className="text-[11px] uppercase tracking-[0.2em] text-white/70 mt-2">
                  A proud division of Empress Collective &amp; Company
                </p>
              </div>
            </div>
          </Reveal>

          <div>
            <Reveal>
              <p className="overline">About Beam Concierge</p>
              <h3 className="mt-5 serif text-[clamp(1.7rem,3vw,2.6rem)] leading-tight tracking-tight text-foreground">
                Over 10 years of hospitality &amp; management taught me the lifeblood of any business:
                <span className="italic text-gold"> customer satisfaction.</span>
              </h3>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-7 space-y-5 text-base leading-relaxed text-muted-foreground max-w-xl">
                <p>
                  I know firsthand how incredible it feels to be recognized for your
                  hard work. That exact empathy and passion built Beam Concierge.
                </p>
                <p>
                  Local providers — from HVAC technicians and contractors to
                  chiropractors and restaurateurs — do incredible work every day, yet
                  so many have a digital storefront that doesn't reflect their reality.
                  Whether it's a lack of recent reviews, a few 1-star comments tanking
                  heavy ad spend, or simply no time to manage feedback, I step in.
                </p>
                <p>
                  By combining smart automation with personalized, manual oversight, I
                  ensure your reputation becomes your most powerful asset — bringing in
                  the data, the growth, and the clientele you deserve.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
